import { useEffect, useMemo, useState } from "react";
import { Search, X, LayoutGrid, Columns3, Rows3 } from "lucide-react";
import { JOB_STATUSES, jobLocation } from "@/components/app/JobCard";

const KEY = "rs_job_filters";
const BLANK = { q: "", status: "", location: "", client: "", view: "grid" };

export function useJobFilters() {
  const [f, setF] = useState(() => {
    try { return { ...BLANK, ...JSON.parse(sessionStorage.getItem(KEY) || "{}") }; } catch (_) { return BLANK; }
  });
  useEffect(() => { try { sessionStorage.setItem(KEY, JSON.stringify(f)); } catch (_) {} }, [f]);
  return [f, setF, () => setF(p => ({ ...BLANK, view: p.view }))];
}

export function applyJobFilters(jobs, f) {
  const q = f.q.trim().toLowerCase();
  return jobs.filter(j => {
    if (f.status && (j.status || "open") !== f.status) return false;
    if (f.location && jobLocation(j) !== f.location) return false;
    if (f.client && (j.client || "") !== f.client) return false;
    if (!q) return true;
    return [j.title, j.description, j.client, j.account, ...(j.must_haves || []), ...(j.nice_to_haves || [])]
      .some(v => (v || "").toLowerCase().includes(q));
  });
}

const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort((a, b) => a.localeCompare(b));
const SEL = "text-sm px-3 py-2 rounded-lg bg-[color:var(--bg-card)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none min-w-0";

export function JobFilters({ jobs, f, setF, clear, shown }) {
  const locations = useMemo(() => uniq(jobs.map(jobLocation)), [jobs]);
  const clients = useMemo(() => uniq(jobs.map(j => j.client)), [jobs]);
  const active = f.q || f.status || f.location || f.client;
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  return (
    <div className="mb-6" data-testid="job-filters">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1 min-w-0">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="job-search" value={f.q} onChange={set("q")} placeholder="Search title, skills, client, account…" className={`${SEL} w-full pl-9`} />
        </div>
        <div className="grid grid-cols-3 lg:flex gap-2">
          <select data-testid="filter-status" value={f.status} onChange={set("status")} className={SEL}>
            <option value="">All statuses</option>
            {JOB_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select data-testid="filter-location" value={f.location} onChange={set("location")} className={SEL}>
            <option value="">All locations</option>
            {locations.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <select data-testid="filter-client" value={f.client} onChange={set("client")} className={SEL}>
            <option value="">All clients</option>
            {clients.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="inline-flex rounded-lg border border-[color:var(--bd-sub)] p-0.5 bg-[color:var(--bg-card)] self-start lg:self-auto" data-testid="view-toggle">
          {[["list", Rows3, "Rows"], ["grid", LayoutGrid, "Grid"], ["board", Columns3, "Board"]].map(([k, Icon, l]) => (
            <button key={k} data-testid={`view-${k}`} onClick={() => setF(p => ({ ...p, view: k }))}
              className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md transition-colors ${f.view === k ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <Icon className="w-4 h-4" />{l}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
        <span data-testid="jobs-count">Showing {shown} of {jobs.length} job{jobs.length === 1 ? "" : "s"}</span>
        {active && (
          <button data-testid="clear-filters" onClick={clear} className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-500 font-medium">
            <X className="w-3 h-3" /> Clear filters
          </button>
        )}
      </div>
    </div>
  );
}
